import { IconNoShow } from 'assets/img';
import { DateTime } from 'luxon';
import { useUndoNoShow } from 'ogp/hooks/queryHooks/shiftclaims/useUndoNoShow';
import { useDialog } from 'ogp/providers/dialog-provider';
import { ContextMenuItem } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import {
  ShiftClaimant,
  ShiftDetailData,
} from 'shared/components/shift-detail-modal/components/tabs/claimants/claimant-table.types';

export const OGPNoShowAction = ({
  candidate,
  shiftData,
  setIsFlexWorker,
  setIsTempWorker,
  openNoShow,
}: {
  candidate: ShiftClaimant;
  shiftData: ShiftDetailData;
  setIsFlexWorker: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTempWorker: React.Dispatch<React.SetStateAction<boolean>>;
  openNoShow: (shiftClaimId: string, shiftId?: string) => void;
}) => {
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();

  const undoNoShowMutation = useUndoNoShow({
    onSuccess: () => {
      closeDialog();
      alertSuccess(`Deze flexwerker is nu niet meer als no-show gemarkeerd`);
    },
    onError: (e) => {
      alertError(e);
    },
    onSettled: () => {
      enableDialog();
    },
  });

  const handleUndoNoShow = (params: {
    shiftClaimId: string;
    firstName: string;
    shiftId: string;
  }) => {
    openDialog(
      {
        title: `Weet je zeker dat je ${params.firstName} niet als no show wilt rapporteren?`,
        content: `Als er nog geen ingediende uren zijn krijgt ${params.firstName} de mogelijkheid om de checkout te starten in de app. Zo niet, dan is de checkout te zien in de checkouts-inbox.`,
      },
      () => {
        disableDialog();
        undoNoShowMutation.mutate({ shiftClaimId: params.shiftClaimId, shiftId: params.shiftId });
      }
    );
  };

  const startDateTime = DateTime.fromISO(`${shiftData.startDate}T${shiftData.startTime}`);
  const allowNoShow = DateTime.now() >= startDateTime.plus({ minutes: 15 });
  const noShowNotAllowedMessage =
    candidate.state === 'checkoutSettled'
      ? 'No-shows melden kan niet meer, deze claim is al gesettled.'
      : 'No-shows melden kan pas 15 minuten na aanvang van de shift.';

  const onContextMenuClick = () => {
    if (candidate.state === 'noShow') {
      handleUndoNoShow({
        shiftClaimId: candidate.claimId,
        firstName: candidate.firstName,
        shiftId: shiftData.id,
      });
      return;
    }
    if (allowNoShow) {
      setIsFlexWorker(candidate.isFlexWorker);
      setIsTempWorker(candidate.employmentType === 'TEMPWORKER');
      openNoShow(candidate.claimId, shiftData.id);
      return;
    }
    alertError(new Error(noShowNotAllowedMessage));
  };

  return (
    <ContextMenuItem onClick={onContextMenuClick} key={`no-show-${candidate.claimId}`}>
      <IconNoShow />
      {candidate.state === 'noShow'
        ? `Undo no show - ${candidate.noShowReason?.shortDescription}`
        : 'No show'}
    </ContextMenuItem>
  );
};
